/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/jsx-no-constructed-context-values */
import React, {
  createContext,
  useCallback,
  useState,
  useContext,
  ReactNode,
  useEffect,
  useRef,
} from "react";
import Cookies from "universal-cookie";
import { useNavigate } from "react-router-dom";
import api from "../services/SecurityApi";
import { validadeToken, logout } from "../services/Auth";
import { TOKEN_IDENTIFICATION, PLANT_ID, COMPANY_ID, USER_NAME, USER_EMAIL } from "../utilities/util";
import { flushActivityLog, resetActivityLog, stopActivityLog } from "../utilities/activityLog";

import IAuthContextData from "../interfaces/IAuthContextData";
import IAuthState from "../interfaces/IAuthState";
// import { useEffect } from "react";
// import { useLocation } from "react-router-dom";
import { useToast } from "./ToastContext";

const AuthContext = createContext<IAuthContextData>({} as IAuthContextData);

const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const cookies = new Cookies();
  const navigate = useNavigate();
  // const location = useLocation();
  const { displayToast } = useToast();
  const defaultToastDuration: number = 2000;

  const ACTIVE_TABS_KEY = "modulare-active-tabs";
  const TAB_ID_STORAGE_KEY = "modulare-auth-tab-id";
  const LOGOUT_DELAY_MS = 4000;
  const tabIdRef = useRef<string>("");
  const pendingLogoutTimeoutRef = useRef<number | null>(null);

  const [data, setData] = useState<IAuthState | null>(() => {
    // Agora só podemos obter o ID do usuário, da empresa e da planta do localStorage
    // O token JWT está nos cookies HttpOnly e não podemos acessá-lo diretamente
    const userId = localStorage.getItem(TOKEN_IDENTIFICATION);
    const plantId = localStorage.getItem(PLANT_ID);
    const companyId = localStorage.getItem(COMPANY_ID);
    // Nome e e-mail são dados de exibição (não sensíveis) persistidos no login;
    // reidratamos aqui para que não desapareçam ao recarregar a página. O token
    // JWT continua exclusivamente nos cookies HttpOnly.
    const userName = localStorage.getItem(USER_NAME) ?? "";
    const userEmail = localStorage.getItem(USER_EMAIL) ?? "";

    if (userId && plantId) {
      return {
        token: null, // Não temos mais acesso ao token
        userId,
        userEmail,
        userName,
        expires: null,
        companyId: companyId ?? "",
        plantId,
      };
    }
    
    return null;
  });

  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
    validadeToken(),
  );

  const cancelScheduledLogout = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (pendingLogoutTimeoutRef.current !== null) {
      window.clearTimeout(pendingLogoutTimeoutRef.current);
      pendingLogoutTimeoutRef.current = null;
    }
  }, []);

  const clearSessionLocally = useCallback(() => {
    setData(null);
    setIsAuthenticated(false);

    cancelScheduledLogout();

    // Remover outros cookies que possam existir do domínio da aplicação
    cookies.remove("reply-security", { path: "/", domain: ".reply.com" });
    cookies.remove("reply-security");
    try {
      localStorage.removeItem(ACTIVE_TABS_KEY);
      localStorage.removeItem(TAB_ID_STORAGE_KEY);
      localStorage.removeItem("modulare-auth-last-session");
      localStorage.removeItem(USER_NAME);
      localStorage.removeItem(USER_EMAIL);
    } catch (error) {
      console.error("Falha ao limpar metadados de sessão:", error);
    }
  }, [cancelScheduledLogout, cookies]);

  const signOut = useCallback(() => {
    // Descarrega o que estiver pendente ANTES de encerrar a sessão: depois do logout o cookie
    // se vai e o lote tomaria 401. O que não couber no envio se perde, e é aceitável — o
    // registro de SAÍDA em si é do servidor, não deste lote.
    void flushActivityLog();
    stopActivityLog();

    clearSessionLocally();

    // Informamos o backend e navegamos sem recarregar a SPA
    logout({
      callServer: true,
      onRedirect: (path) => navigate(path, { replace: true }),
    });
  }, [clearSessionLocally, navigate]);

  const ensureTabId = useCallback((): string => {
    if (tabIdRef.current) {
      return tabIdRef.current;
    }

    if (typeof window === "undefined") {
      tabIdRef.current = `${Date.now()}-${Math.random()}`;
      return tabIdRef.current;
    }

    try {
      const existing = sessionStorage.getItem(TAB_ID_STORAGE_KEY);
      if (existing) {
        tabIdRef.current = existing;
        return existing;
      }

      const newId = typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random()}`;

      sessionStorage.setItem(TAB_ID_STORAGE_KEY, newId);
      tabIdRef.current = newId;
      return newId;
    } catch (error) {
      const fallbackId = `${Date.now()}-${Math.random()}`;
      tabIdRef.current = fallbackId;
      return fallbackId;
    }
  }, []);

  const readActiveTabs = useCallback((): string[] => {
    if (typeof window === "undefined") {
      return [];
    }
    try {
      const raw = localStorage.getItem(ACTIVE_TABS_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((item) => typeof item === "string");
    } catch (error) {
      console.error("Falha ao ler tabs ativas:", error);
      return [];
    }
  }, []);

  const writeActiveTabs = useCallback((tabs: string[]) => {
    if (typeof window === "undefined") {
      return;
    }
    try {
      localStorage.setItem(ACTIVE_TABS_KEY, JSON.stringify(tabs));
    } catch (error) {
      console.error("Falha ao atualizar tabs ativas:", error);
    }
  }, []);

  const scheduleLogoutIfNoTabs = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }

    cancelScheduledLogout();

    pendingLogoutTimeoutRef.current = window.setTimeout(() => {
      pendingLogoutTimeoutRef.current = null;
      const tabs = readActiveTabs();
      if (tabs.length === 0) {
        logout({
          callServer: true,
          useBeacon: true,
          onRedirect: () => {},
        });
      }
    }, LOGOUT_DELAY_MS);
  }, [LOGOUT_DELAY_MS, cancelScheduledLogout, readActiveTabs]);

  const registerTabAsActive = useCallback(() => {
    const tabId = ensureTabId();
    const tabs = readActiveTabs();
    if (!tabs.includes(tabId)) {
      tabs.push(tabId);
      writeActiveTabs(tabs);
    }
    cancelScheduledLogout();
    try {
      localStorage.setItem("modulare-auth-last-session", JSON.stringify({
        timestamp: Date.now(),
        userId: localStorage.getItem(TOKEN_IDENTIFICATION),
      }));
    } catch (error) {
      console.error("Falha ao persistir metadados de sessão:", error);
    }
  }, [ensureTabId, readActiveTabs, writeActiveTabs, cancelScheduledLogout]);

  const unregisterTab = useCallback((): string[] => {
    const tabId = ensureTabId();
    const tabs = readActiveTabs();
    const updated = tabs.filter((item) => item !== tabId);
    writeActiveTabs(updated);
    return updated;
  }, [ensureTabId, readActiveTabs, writeActiveTabs]);

  useEffect(() => {
    if (!isAuthenticated) {
      return undefined;
    }

    registerTabAsActive();

    const syncAuthState = (): void => {
      const stillAuthenticated = validadeToken();
      if (stillAuthenticated !== isAuthenticated) {
        setIsAuthenticated(stillAuthenticated);
      }
    };

    const handleBeforeUnload = (): void => {
      const remainingTabs = unregisterTab();

      if (remainingTabs.length === 0) {
        scheduleLogoutIfNoTabs();
      } else {
        cancelScheduledLogout();
      }
    };

    const handleStorage = (event: StorageEvent): void => {
      if (event.key !== ACTIVE_TABS_KEY) {
        return;
      }

      const tabs = readActiveTabs();
      if (tabs.length === 0) {
        scheduleLogoutIfNoTabs();
      } else {
        cancelScheduledLogout();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("storage", handleStorage);
    window.addEventListener("focus", syncAuthState);
    window.addEventListener("pageshow", syncAuthState);
    document.addEventListener("visibilitychange", syncAuthState);

    syncAuthState();

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("focus", syncAuthState);
      window.removeEventListener("pageshow", syncAuthState);
      document.removeEventListener("visibilitychange", syncAuthState);

      const remainingTabs = unregisterTab();

      if (remainingTabs.length === 0) {
        scheduleLogoutIfNoTabs();
      } else {
        cancelScheduledLogout();
      }

      syncAuthState();
    };
  }, [
    isAuthenticated,
    registerTabAsActive,
    unregisterTab,
    readActiveTabs,
    validadeToken,
    scheduleLogoutIfNoTabs,
    cancelScheduledLogout,
  ]);

  // Removido o useEffect que fazia chamada ao api.GetEnvironment(), pois esse endpoint não existe na API.

  const signIn = async (userName: string, password: string): Promise<void> => {
    await api
      .Login(userName, password)
      .then((userResult: any) => {
        try {
          // O token agora está nos cookies HttpOnly e não podemos acessá-lo diretamente
          // Mas ainda podemos acessar os dados do usuário retornados pela API
          if (!userResult.data.data.userToken) {
            displayToast(
              "error",
              "Falha no login",
              userResult?.data?.message ?? "Senha ou Email Incorreto",
              false,
              defaultToastDuration,
            );
            return;
          }
        } catch (err) {
          displayToast(
            "error",
            "Falha no login",
            userResult?.data?.message ?? "Senha ou Email Incorreto",
            false,
            defaultToastDuration,
          );
          return;
        }

        const { userToken } = userResult.data.data;

        const userId = userToken.id;
        const userEmail = userToken.email;
        
        // Extrair informações das claims
        let companyId = "";
        let plantId = "";
        let userName = "";
        
        userToken.claims.forEach((claim: any) => {
          if (claim.type === "companyId") {
            companyId = claim.value;
          } else if (claim.type === "plantId") {
            plantId = claim.value;
          } else if (claim.type === "sub") {
            userName = claim.value;
          }
        });

        // Armazenamos apenas o ID do usuário, da empresa e da planta no localStorage
        // O token JWT agora é gerenciado pelo navegador via cookies HttpOnly
        localStorage.setItem(TOKEN_IDENTIFICATION, userId);
        localStorage.setItem(PLANT_ID, plantId);
        localStorage.setItem(USER_NAME, userName ?? "");
        localStorage.setItem(USER_EMAIL, userEmail ?? "");
        if (companyId) {
          localStorage.setItem(COMPANY_ID, companyId);
        } else {
          localStorage.removeItem(COMPANY_ID);
        }
        setData({
          token: null, // Não temos mais acesso ao token
          expires: null,
          userId,
          userEmail,
          userName,
          companyId,
          plantId,
        });
        setIsAuthenticated(true);
        // Rearma o coletor e abre uma sessão de atividade nova. Necessário porque um 401 anterior
        // o desarma de propósito — sem este reset, quem tivesse a sessão expirada numa aba ficaria
        // sem telemetria pelo resto da vida daquela aba, mesmo depois de logar de novo.
        resetActivityLog();
        navigate("/");
      })
      .catch((e: any) => {
        console.log("e", e);
        displayToast(
          "error",
          "Falha no login",
          e.response.data.error ?? "Senha ou Email Incorreto",
          false,
          defaultToastDuration,
        );
        clearSessionLocally();
      });
  };

  return (
    <AuthContext.Provider
      value={{
        userEmail: data?.userEmail ?? "",
        userId: data?.userId ?? "",
        userName: data?.userName ?? "",
        signIn,
        signOut,
        companyId: data?.companyId ?? "",
        plantId: data?.plantId ?? "",
        isAuthenticated,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): IAuthContextData {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
export { AuthProvider, useAuth };
